/**
 * Push Notification Service
 * Stores and removes FCM token using authenticated API client
 */

import { Capacitor } from '@capacitor/core';
import { getAxiosErrorMessage } from '../utils/error';
import api from './api';

interface PushTokenResponse {
  success: boolean;
  error?: string;
  push_enabled?: boolean;
}

export type PushPlatform = 'android' | 'ios';
interface StorePushTokenPayload {
  fcm_token: string;
  platform: PushPlatform;
  timezone?: string;
  /**
   * RING-PUSH: this build draws the incoming-call notification itself
   * (android IncomingCallMessagingService, Join / Not now), so the server
   * may send it data-only. The web assets ship inside the APK, so the flag
   * and the native service always come in the same build. Only a real
   * Android build says so (getPlatform() below maps anything not iOS, the
   * web included, to 'android'; #1009 item 3).
   */
  call_notifications?: boolean;
}

/**
 * Detect platform dynamically
 */
const getPlatform = (): PushPlatform => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' ? 'ios' : 'android';
};

/**
 * Store FCM token in backend
 */
export const storePushToken = async (fcm_token: string): Promise<PushTokenResponse> => {
  try {
    const platform = getPlatform();
    const payload: StorePushTokenPayload = {
      fcm_token,
      platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      call_notifications: Capacitor.getPlatform() === 'android',
    };

    await api.post('/push-token', payload);
    return { success: true };
  } catch (error) {
    console.error('Token store error:', error);

    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to store push token', {
        log: 'PushNotificationService.storePushToken',
      }),
    };
  }
};

/**
 * Get push notification status while after allow notification
 */
export const getPushStatus = async () => {
  const response = await api.get('/push-token');
  return response.data;
};

/**
 * Update push enabled status
 */
export const updatePushStatus = async (push_enabled: boolean): Promise<PushTokenResponse> => {
  try {
    await api.patch('/push-token', {
      push_enabled,
    });
    return {
      success: true,
      push_enabled,
    };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to update push status', {
        log: 'PushNotificationService.updatePushStatus',
      }),
    };
  }
};

/**
 * Update a single notification preference for the authenticated user.
 *
 * @param category - Notification category: 'realtime' | 'nudge_sequence' | 'daily_digest' | 'empathy_queue'
 * @param type - Specific notification type e.g. 'match_found'. Pass null for category-level toggle.
 * @param enabled - Whether the notification should be enabled
 */
export const updateNotificationPreference = async (
  category: string,
  type: string | null,
  enabled: boolean,
) => {
  const response = await api.patch('/notification-preferences', {
    category,
    type,
    enabled,
  });
  return response.data;
};

/**
 * Report that a notification was opened/tapped by the user.
 * Fires and forgets — failure is non-critical.
 */
export const reportNotificationOpened = async (type: string, channel?: 'web'): Promise<void> => {
  try {
    // A web push is logged as a delivery attempt, not a notification log row,
    // so the server is told which channel's row to mark opened.
    await api.post('/notification-opened', channel ? { type, channel } : { type });
  } catch {
    // Non-critical — silently ignore
  }
};
